import { useState, useEffect, useContext, createContext } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // D'abord vérifier localement
      if (authService.isAuthenticated()) {
        const userData = authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        // Si pas de données locales, vérifier avec l'API
        try {
          const response = await authService.checkAuthStatus();
          if (response && response.authenticated) {
            setUser(response.user);
            setIsAuthenticated(true);
          }
        } catch (apiError) {
          // L'erreur 401 est normale si non connecté, ne pas la logger comme erreur
          if (apiError.message.includes('401') || 
              apiError.message.includes('non fournies') ||
              apiError.message.includes('Unauthorized')) {
            console.log('Utilisateur non connecté (normal)');
          } else {
            console.error('Erreur vérification statut API:', apiError);
          }
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    } catch (error) {
      console.error('Erreur vérification authentification:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    setIsLoading(true);
    try {
      const response = await authService.login(credentials);
      setUser(response.user);
      setIsAuthenticated(true);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    setIsLoading(true);
    try {
      const response = await authService.register(userData);
      
      // Se connecter automatiquement après l'inscription
      const identifier = userData.email || userData.phone;
      await login({
        identifier: identifier,
        password: userData.password
      });
      
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    console.log('useAuth logout called');
    setIsLoading(true);
    try {
      console.log('Calling authService.logout...');
      await authService.logout();
      console.log('authService.logout completed, clearing state...');
      setUser(null);
      setIsAuthenticated(false);
      console.log('State cleared');
    } catch (error) {
      console.error('Error in logout:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    authService.updateUser(userData);
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}